// src/components/BannersManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api'; // Import your existing API service

// Styled components
const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '4px',
});

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 1 
    ? theme.palette.success.main 
    : status === 0 
      ? theme.palette.error.main 
      : theme.palette.warning.main,
  color: '#fff',
  fontWeight: 600,
}));

const ImagePreview = styled(Box)({
  width: 100,
  height: 100,
  borderRadius: 8,
  overflow: 'hidden',
  border: '2px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f5f5f5',
});

const BannersManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    imageurl: '',
    title: '',
    alt_text: '',
    status: 1,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    bannerId: null,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchBanners = async (page = pagination.page) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/banners', {
        params: {
          page,
          limit: pagination.limit,
        },
      });
      
      if (response.data.success) {
        setBanners(response.data.data);
        setPagination({
          ...pagination,
          page: response.data.pagination.page,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      showSnackbar('Failed to fetch banners', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        imageurl: banner.imageurl || '',
        title: banner.title || '',
        alt_text: banner.alt_text || '',
        status: banner.status !== undefined ? banner.status : 1,
      });
      setImagePreview(banner.imageurl);
    } else {
      setEditingBanner(null);
      setFormData({
        imageurl: '',
        title: '',
        alt_text: '',
        status: 1,
      });
      setImagePreview(null);
      setImageFile(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBanner(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSaveBanner = async () => {
    try {
      if (!formData.imageurl && !imageFile) {
        showSnackbar('Please select an image', 'error');
        return;
      }
      if (!formData.title) {
        showSnackbar('Please enter a title', 'error');
        return;
      }

      let imageUrl = formData.imageurl;

      if (imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('banner_image', imageFile);
        const uploadResponse = await api.post(
          '/admin/banners/upload',
          formDataUpload,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        if (uploadResponse.data.success) {
          imageUrl = uploadResponse.data.data.image_url;
        } else {
          showSnackbar('Failed to upload image', 'error');
          return;
        }
      }

      const bannerData = {
        ...formData,
        imageurl: imageUrl,
      };

      let response;
      if (editingBanner) {
        response = await api.put(
          `/admin/banners/${editingBanner.id}`,
          bannerData
        );
        if (response.data.success) {
          showSnackbar('Banner updated successfully');
          fetchBanners();
        }
      } else {
        response = await api.post('/admin/banners', bannerData);
        if (response.data.success) {
          showSnackbar('Banner created successfully');
          fetchBanners();
        }
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving banner:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to save banner',
        'error'
      );
    }
  };

  const handleDeleteBanner = async () => {
    try {
      const response = await api.delete(
        `/admin/banners/${deleteDialog.bannerId}`
      );
      if (response.data.success) {
        showSnackbar('Banner deleted successfully');
        fetchBanners();
        setDeleteDialog({ open: false, bannerId: null });
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      showSnackbar('Failed to delete banner', 'error');
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      const newStatus = banner.status === 1 ? 0 : 1;
      const response = await api.put(
        `/admin/banners/${banner.id}`,
        {
          ...banner,
          status: newStatus,
        }
      );
      if (response.data.success) {
        showSnackbar(`Banner ${newStatus === 1 ? 'activated' : 'deactivated'}`);
        fetchBanners();
      }
    } catch (error) {
      console.error('Error toggling banner status:', error);
      showSnackbar('Failed to update banner status', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Banners Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add New Banner
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Alt Text</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Loading banners...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : banners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="textSecondary">
                    No banners found. Click "Add New Banner" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              banners.map((banner) => (
                <TableRow key={banner.id} hover>
                  <TableCell>
                    <ImagePreview>
                      {banner.imageurl ? (
                        <StyledImage src={banner.imageurl} alt={banner.alt_text || banner.title} />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40, color: '#ccc' }} />
                      )}
                    </ImagePreview>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {banner.title || 'Untitled'}
                    </Typography>
                  </TableCell>
                  <TableCell>{banner.alt_text || '-'}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={banner.status === 1 ? 'Active' : 'Inactive'}
                      status={banner.status}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(banner.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(banner.updated_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(banner)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Toggle Status">
                        <IconButton
                          size="small"
                          color={banner.status === 1 ? 'error' : 'success'}
                          onClick={() => handleToggleStatus(banner)}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, bannerId: banner.id })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(_, page) => fetchBanners(page)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingBanner ? 'Edit Banner' : 'Add New Banner'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Banner Image *
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => document.getElementById('image-upload').click()}
              >
                {imagePreview ? (
                  <ImagePreview sx={{ width: '100%', height: 200 }}>
                    <StyledImage src={imagePreview} alt="Preview" />
                  </ImagePreview>
                ) : (
                  <>
                    <ImageIcon sx={{ fontSize: 48, color: '#ccc' }} />
                    <Typography variant="body2" color="textSecondary">
                      Click to upload image
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Recommended: 1920x600px
                    </Typography>
                  </>
                )}
              </Box>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Box>

            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              fullWidth
              required
            />

            <TextField
              label="Alt Text"
              name="alt_text"
              value={formData.alt_text}
              onChange={handleInputChange}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.status === 1}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.checked ? 1 : 0 })
                  }
                />
              }
              label={formData.status === 1 ? 'Active' : 'Inactive'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBanner}>
            {editingBanner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, bannerId: null })}
        maxWidth="xs"
      >
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, bannerId: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteBanner}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannersManagement;